/* eslint-disable max-lines */
/**
 * Coding Panel Component (Right Panel)
 *
 * Three-tab panel for coding operations:
 * - Coding Tab: Quick code input, applied codes list, notes
 * - Intelligence Tab: Document-level metrics, valence distribution
 * - Entities Tab: Entity profiles with radar charts (placeholder for Stage 4)
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  LinearProgress,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  VALENCE_COLORS,
  TABS,
  DEFAULT_CONFIDENCE,
  MAX_NOTES_LENGTH,
  DIMENSION_COLORS
} from '../../constants/workbenchConfig';
import { AISuggestionsList } from './AISuggestionCard';
import BehavioralScoreCard from './BehavioralScoreCard';

/**
 * Coding Panel with tabs
 */
function CodingPanel({
  codes,
  chunks,
  currentChunk,
  onApplyCode,
  onRemoveCode,
  onUpdateCode,
  learningMode,
  documentProgress,
  // AI suggestion props
  aiSuggestions,
  aiLoading,
  aiError,
  processingAiAction,
  onFetchAISuggestions,
  onAcceptSuggestion,
  onRejectSuggestion,
  onAcceptAllSuggestions,
  // Entity scoring props
  entityId,
  entityName,
  // AI fetch state
  hasFetched,
}) {
  const [activeTab, setActiveTab] = useState(TABS.CODING);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tab Header */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 42,
          '& .MuiTab-root': {
            minHeight: 42,
            textTransform: 'none',
            fontWeight: 500,
          },
        }}
      >
        <Tab label="Coding" />
        <Tab label="Intelligence" />
        <Tab label="Entities" />
      </Tabs>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === TABS.CODING && (
          <CodingTab
            codes={codes}
            currentChunk={currentChunk}
            onApplyCode={onApplyCode}
            onRemoveCode={onRemoveCode}
            onUpdateCode={onUpdateCode}
            learningMode={learningMode}
            aiSuggestions={aiSuggestions}
            aiLoading={aiLoading}
            aiError={aiError}
            processingAiAction={processingAiAction}
            onFetchAISuggestions={onFetchAISuggestions}
            onAcceptSuggestion={onAcceptSuggestion}
            onRejectSuggestion={onRejectSuggestion}
            onAcceptAllSuggestions={onAcceptAllSuggestions}
            hasFetched={hasFetched}
          />
        )}
        {activeTab === TABS.INTELLIGENCE && (
          <IntelligenceTab
            chunks={chunks}
            codes={codes}
            currentChunk={currentChunk}
            documentProgress={documentProgress}
          />
        )}
        {activeTab === TABS.ENTITIES && (
          <EntitiesTab
            currentChunk={currentChunk}
            entityId={entityId}
            entityName={entityName}
          />
        )}
      </Box>
    </Box>
  );
}

/**
 * Coding Tab - Quick code input and applied codes
 */
// eslint-disable-next-line max-lines-per-function
function CodingTab({
  codes,
  currentChunk,
  onApplyCode,
  onRemoveCode,
  onUpdateCode,
  learningMode,
  // AI props
  aiSuggestions,
  aiLoading,
  aiError,
  processingAiAction,
  onFetchAISuggestions,
  onAcceptSuggestion,
  onRejectSuggestion,
  onAcceptAllSuggestions,
  hasFetched,
}) {
  const [inputValue, setInputValue] = useState('');
  const [notes, setNotes] = useState('');
  const [editingCode, setEditingCode] = useState(null);

  const appliedCodes = currentChunk?.behavioral_codes || [];
  const hasAiSuggestions = aiSuggestions && aiSuggestions.length > 0;

  // Handle code selection from autocomplete
  const handleCodeSelect = async (event, code) => {
    if (code) {
      await onApplyCode(code.code, {
        confidence: DEFAULT_CONFIDENCE,
        notes: '',
        advanceToNext: false,
      });
      setInputValue('');
    }
  };

  // Handle notes save
  const handleNotesSave = async (codeName) => {
    if (editingCode === codeName) {
      await onUpdateCode(codeName, { notes });
      setEditingCode(null);
      setNotes('');
    }
  };

  if (!currentChunk) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Select a chunk to apply behavioral codes
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Quick Code Input */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Quick Code
        </Typography>
        <Autocomplete
          freeSolo
          options={codes}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.displayName
          }
          inputValue={inputValue}
          onInputChange={(e, v) => setInputValue(v)}
          onChange={handleCodeSelect}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Type to search codes..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: VALENCE_COLORS[option.valence],
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">{option.displayName}</Typography>
                {learningMode && (
                  <Typography variant="caption" color="text.secondary">
                    {option.description?.substring(0, 80)}...
                  </Typography>
                )}
              </Box>
              {option.shortcut && (
                <Chip
                  label={option.shortcut}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          )}
        />
      </Box>

      {/* AI Suggestions Section */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 16, color: '#0097B2' }} />
            AI Suggestions
            {hasAiSuggestions && (
              <Chip
                label={aiSuggestions.length}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: 'rgba(0, 151, 178, 0.15)',
                  color: '#0097B2',
                  ml: 0.5,
                }}
              />
            )}
          </Typography>
          <Tooltip title="Get AI suggestions for this chunk (G)">
            <IconButton
              size="small"
              onClick={() => onFetchAISuggestions?.({ force: true })}
              disabled={aiLoading || !currentChunk}
              sx={{
                bgcolor: 'rgba(0, 151, 178, 0.08)',
                '&:hover': { bgcolor: 'rgba(0, 151, 178, 0.15)' },
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 18, color: '#0097B2' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* AI Error */}
        {aiError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mb: 1 }}
          >
            {aiError}
          </Typography>
        )}

        {/* AI Suggestions List */}
        <AISuggestionsList
          suggestions={aiSuggestions}
          onAccept={onAcceptSuggestion}
          onReject={onRejectSuggestion}
          onAcceptAll={onAcceptAllSuggestions}
          learningMode={learningMode}
          isLoading={aiLoading}
          processingId={processingAiAction}
          hasFetched={hasFetched}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Applied Codes List */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Applied Codes ({appliedCodes.length})
        </Typography>

        {appliedCodes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No codes applied to this chunk
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {appliedCodes.map((code) => (
              <AppliedCodeCard
                key={code.code}
                code={code}
                onRemove={() => onRemoveCode(code.code)}
                onEditNotes={() => {
                  setEditingCode(code.code);
                  setNotes(code.notes || '');
                }}
                isEditing={editingCode === code.code}
                notes={notes}
                onNotesChange={setNotes}
                onNotesSave={() => handleNotesSave(code.code)}
                onNotesCancel={() => {
                  setEditingCode(null);
                  setNotes('');
                }}
                learningMode={learningMode}
                codeDetails={codes.find((c) => c.code === code.code)}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Applied Code Card
 */
// eslint-disable-next-line max-lines-per-function
function AppliedCodeCard({
  code,
  onRemove,
  onEditNotes,
  isEditing,
  notes,
  onNotesChange,
  onNotesSave,
  onNotesCancel,
  learningMode,
  codeDetails,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        bgcolor: 'rgba(0,0,0,0.02)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      {/* Code Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: VALENCE_COLORS[code.valence],
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {code.code.replace(/_/g, ' ')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit notes">
            <IconButton size="small" onClick={onEditNotes}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove code">
            <IconButton size="small" onClick={onRemove}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* Confidence */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Confidence: {Math.round((code.confidence || 0) * 100)}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(code.confidence || 0) * 100}
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            bgcolor: 'rgba(0,0,0,0.08)',
            '& .MuiLinearProgress-bar': {
              bgcolor: VALENCE_COLORS[code.valence],
            },
          }}
        />
      </Box>
      {/* Learning Mode: Show code description */}
      {learningMode && codeDetails?.description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {codeDetails.description}
        </Typography>
      )}
      {/* Notes Editing */}
      {isEditing ? (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Add notes..."
            inputProps={{ maxLength: MAX_NOTES_LENGTH }}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {notes.length}/{MAX_NOTES_LENGTH}
            </Typography>
            <Chip
              label="Cancel"
              size="small"
              onClick={onNotesCancel}
              sx={{ height: 24, fontSize: '0.7rem' }}
            />
            <Chip
              label="Save"
              size="small"
              color="primary"
              onClick={onNotesSave}
              sx={{ height: 24, fontSize: '0.7rem' }}
            />
          </Box>
        </Box>
      ) : (
        code.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            "{code.notes}"
          </Typography>
        )
      )}
    </Paper>
  );
}

/**
 * Intelligence Tab - Document-level metrics
 */
 
// eslint-disable-next-line max-lines-per-function
function IntelligenceTab({ chunks, codes, documentProgress }) {
  // Aggregate all behavioral codes from all chunks
  const { valenceDistribution, topCodes, totalCodes } = useMemo(() => {
    const valence = { positive: 0, negative: 0, neutral: 0 };
    const codeFrequency = {};
    let total = 0;

    (chunks || []).forEach((chunk) => {
      (chunk.behavioral_codes || []).forEach((bc) => {
        const v = bc.valence || 'neutral';
        valence[v] = (valence[v] || 0) + 1;
        codeFrequency[bc.code] = (codeFrequency[bc.code] || 0) + 1;
        total++;
      });
    });

    // Sort codes by frequency, take top 10
    const sorted = Object.entries(codeFrequency)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([code, count]) => {
        const codeDetail = (codes || []).find((c) => c.code === code);
        return {
          code,
          displayName: codeDetail?.displayName || code.replace(/_/g, ' '),
          valence: codeDetail?.valence || 'neutral',
          dimension: codeDetail?.primaryDimension,
          count,
        };
      });

    return { valenceDistribution: valence, topCodes: sorted, totalCodes: total };
  }, [chunks, codes]);

  const maxCodeCount = topCodes.length > 0 ? topCodes[0].count : 1;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Document Intelligence
      </Typography>

      {/* Progress Overview */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: 'rgba(0,0,0,0.02)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Coding Progress
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {documentProgress.percentage}%
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ({documentProgress.coded}/{documentProgress.total} chunks)
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={documentProgress.percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'rgba(0,0,0,0.08)',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'accent.teal',
              borderRadius: 4,
            },
          }}
        />
      </Paper>

      {/* Valence Distribution */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: 'rgba(0,0,0,0.02)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Valence Distribution ({totalCodes} total applications)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {['positive', 'negative', 'neutral'].map((valence) => (
            <Box key={valence} sx={{ flex: 1, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: VALENCE_COLORS[valence],
                  mx: 'auto',
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                  {valenceDistribution[valence]}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                {valence}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Top Codes */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: 'rgba(0,0,0,0.02)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Top Codes
        </Typography>
        {topCodes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No codes applied yet
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {topCodes.map((item) => (
              <Box key={item.code} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: VALENCE_COLORS[item.valence],
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ flex: 1, fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {item.displayName}
                </Typography>
                <Box sx={{ width: 60, flexShrink: 0 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(item.count / maxCodeCount) * 100}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'rgba(0,0,0,0.06)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: DIMENSION_COLORS[item.dimension] || VALENCE_COLORS[item.valence],
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ width: 20, textAlign: 'right' }}>
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Typography variant="caption" color="text.secondary">
        <InfoIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
        Metrics update as you code more chunks.
      </Typography>
    </Box>
  );
}

/**
 * Entities Tab - Entity profiles with radar charts
 */
function EntitiesTab({ entityId, entityName }) {
  return (
    <Box sx={{ p: 2 }}>
      <BehavioralScoreCard entityId={entityId} entityName={entityName} />
    </Box>
  );
}

export default CodingPanel;
